import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { parseSpreadsheetFile } from './parse';
import { ENTITY_CONFIGS } from './entityConfigs';
import { C, css } from '../Dashboard';

// The file viewer below is a fixed dark overlay regardless of the org's theme
// (same treatment as BulkImportModal), so it keeps its own hardcoded palette.
// The inline history table above it sits directly in the themed page, so it
// must use the live C tokens -- a hardcoded palette there went invisible
// (near-white text) on light-themed orgs.
const W = { bg: '#0A1526', surface: '#16233F', border: '#28395C', muted: '#7d8ea2', bright: '#f1f5f9', accent: '#00A0C8', danger: '#f87171', success: '#68d391', amber: '#f6c26b' };
const btn = (variant) => ({
  padding: '6px 12px', borderRadius: '6px', border: variant === 'ghost' ? `1px solid ${W.border}` : 'none',
  background: variant === 'primary' ? W.accent : variant === 'ghost' ? 'transparent' : W.border,
  color: variant === 'primary' ? '#fff' : variant === 'ghost' ? W.muted : W.bright,
  fontSize: '12px', fontWeight: '600', cursor: 'pointer',
});

export default function ImportHistory({ org, refreshKey }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null); // an import_logs row, or null

  useEffect(() => {
    let canceled = false;
    setLoading(true);
    supabase.from('import_logs')
      .select('*, profiles(name)')
      .eq('org_id', org.id)
      .order('created_at', { ascending: false })
      .limit(50)
      .then(({ data, error }) => {
        if (canceled) return;
        if (error) console.error('Loading import history failed:', error);
        setLogs(data || []);
        setLoading(false);
      });
    return () => { canceled = true; };
  }, [org.id, refreshKey]);

  if (loading) return null;
  if (logs.length === 0) return null;

  // Read from C (not module scope) so these track the org's live theme --
  // applyTheme mutates C's fields in place after this module has already loaded.
  const statusCfg = {
    success: { label: 'Success', bg: 'rgba(104,211,145,0.12)', color: C.success },
    partial: { label: 'Partial', bg: 'rgba(246,194,107,0.12)', color: C.badgeAmber },
    failed:  { label: 'Failed',  bg: 'rgba(248,113,113,0.12)', color: C.danger },
  };
  const th = { padding: '8px 10px', textAlign: 'left', color: C.muted, fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' };
  const td = { padding: '8px 10px', color: C.textBright };

  return (
    <div style={{ marginTop: '28px' }}>
      <div style={{ fontSize: '13px', fontWeight: '700', color: C.textBright, marginBottom: '4px' }}>Import History</div>
      <div style={{ fontSize: '11px', color: C.muted, marginBottom: '10px' }}>Last {logs.length} import{logs.length === 1 ? '' : 's'}. Click a row to see everything that was in the file.</div>
      <div style={{ border: `1px solid ${C.border}`, borderRadius: '8px', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <thead>
            <tr style={{ background: C.bg }}>
              <th style={th}>Date</th>
              <th style={th}>Type</th>
              <th style={th}>File</th>
              <th style={th}>By</th>
              <th style={th}>Status</th>
              <th style={{ ...th, textAlign: 'right' }}>Created</th>
              <th style={{ ...th, textAlign: 'right' }}>Updated</th>
              <th style={{ ...th, textAlign: 'right' }}>Skipped</th>
              <th style={{ ...th, textAlign: 'right' }}>Errors</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {logs.map(l => {
              const s = statusCfg[l.status] || statusCfg.failed;
              return (
                <tr key={l.id} style={{ borderTop: `1px solid ${C.border}` }}>
                  <td style={td}>{new Date(l.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</td>
                  <td style={td}>{ENTITY_CONFIGS[l.entity_type]?.label || l.entity_type}</td>
                  <td style={{ ...td, maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={l.file_name}>{l.file_name}</td>
                  <td style={td}>{l.profiles?.name || '—'}</td>
                  <td style={td}><span style={{ fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '4px', background: s.bg, color: s.color }}>{s.label}</span></td>
                  <td style={{ ...td, textAlign: 'right', color: C.success }}>{l.created_count || 0}</td>
                  <td style={{ ...td, textAlign: 'right', color: C.accent }}>{l.updated_count || 0}</td>
                  <td style={{ ...td, textAlign: 'right', color: C.muted }}>{l.skipped_count || 0}</td>
                  <td style={{ ...td, textAlign: 'right', color: l.error_count ? C.danger : C.muted }}>{l.error_count || 0}</td>
                  <td style={td}><button style={css.btn('ghost')} onClick={() => setViewing(l)}>View File</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {viewing && <ImportFileViewer log={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

const thDark = { padding: '8px 10px', textAlign: 'left', color: W.muted, fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px' };
const tdDark = { padding: '8px 10px', color: W.bright };

function ImportFileViewer({ log, onClose }) {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [parsed, setParsed] = useState(null); // { headers, rows }
  const [signedUrl, setSignedUrl] = useState(null);

  useEffect(() => {
    let canceled = false;
    (async () => {
      const { data: signed, error: signErr } = await supabase.storage.from('import-files').createSignedUrl(log.file_path, 300);
      if (canceled) return;
      if (signErr || !signed?.signedUrl) { setErr('Could not load the original file — it may have been removed.'); setLoading(false); return; }
      setSignedUrl(signed.signedUrl);
      try {
        const res = await fetch(signed.signedUrl);
        const blob = await res.blob();
        const file = new File([blob], log.file_name);
        const result = await parseSpreadsheetFile(file);
        if (canceled) return;
        setParsed(result);
      } catch (e) {
        if (!canceled) setErr('Could not read the file contents.');
      }
      if (!canceled) setLoading(false);
    })();
    return () => { canceled = true; };
  }, [log.file_path]); // eslint-disable-line

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3100, padding: '20px' }}>
      <div style={{ background: W.surface, border: `1px solid ${W.border}`, borderRadius: '14px', width: '100%', maxWidth: '920px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '18px 22px', borderBottom: `1px solid ${W.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: '15px', fontWeight: '800', color: W.bright }}>{log.file_name}</div>
            <div style={{ fontSize: '11px', color: W.muted, marginTop: '3px' }}>
              {ENTITY_CONFIGS[log.entity_type]?.label || log.entity_type} import — {new Date(log.created_at).toLocaleString()}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: W.muted, fontSize: '22px', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: '18px 22px', overflow: 'auto', flex: 1 }}>
          {loading && <div style={{ color: W.muted, fontSize: '13px', textAlign: 'center', padding: '30px' }}>Loading…</div>}
          {err && <div style={{ color: W.danger, fontSize: '13px' }}>{err}</div>}
          {parsed && (
            <>
              <div style={{ fontSize: '11px', color: W.muted, marginBottom: '10px' }}>
                {parsed.rows.length} row{parsed.rows.length === 1 ? '' : 's'}, exactly as uploaded — not what was mapped or imported.
                {signedUrl && <> · <a href={signedUrl} download={log.file_name} style={{ color: W.accent, textDecoration: 'none' }}>Download original file ↓</a></>}
              </div>
              <div style={{ overflow: 'auto', border: `1px solid ${W.border}`, borderRadius: '8px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead style={{ position: 'sticky', top: 0, background: W.bg }}>
                    <tr>{parsed.headers.map((h, i) => <th key={i} style={{ ...thDark, whiteSpace: 'nowrap' }}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {parsed.rows.map((row, ri) => (
                      <tr key={ri} style={{ borderTop: `1px solid ${W.border}` }}>
                        {row.map((cell, ci) => <td key={ci} style={{ ...tdDark, whiteSpace: 'nowrap' }}>{cell || <span style={{ color: W.muted, fontStyle: 'italic' }}>—</span>}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
        <div style={{ padding: '14px 22px', borderTop: `1px solid ${W.border}`, display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
          <button onClick={onClose} style={btn('primary')}>Close</button>
        </div>
      </div>
    </div>
  );
}
